/**
 * PeriodMapsSection — PMA-COCK-188 (client wave 2026-09-13): "Colocar mapas
 * de calor y mapa de control que sea en el primer tiempo separado del
 * segundo tiempo" — one block per period holding at least one located
 * event, each with both teams' heat maps and the team control map, filtered
 * to that period's own events only. No per-player view, no toggle — always
 * team-aggregate, matching the client's mock. Rendered on both surfaces
 * (`AnalyticsView.tsx`, `PublicMatchAnalytics.tsx`) ahead of the existing,
 * unchanged whole-match "Generales" block (`HeatMapSection`), and — for the
 * cockpit's `MatchAnalytics.exportPdf` — captured from the real, on-screen
 * DOM via the same `getRenderedPeriods`/`PERIOD_MAPS_LABEL_KEYS` this file
 * exports, so screen and PDF can never disagree about which periods exist.
 */
import { useMemo, type FC, type ReactNode } from "react";
import SoccerFieldHeatMap from "./SoccerFieldHeatMap";
import TeamControlMap from "./TeamControlMap";
import TeamPitchRows from "./TeamPitchRows";
import {
  computeHeatMapData,
  extractHeatPoints,
} from "../../utils/heatMapZones";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match } from "../../types";
import SectionGate from "../../../../components/match/SectionGate";
import type {
  ShareSectionKey,
  ShareUpsell,
  ShareVisibilityState,
} from "../../../../lib/shareVisibility";

/** The four periods a match can carry located events for (PMA-COCK-163). */
export type RenderablePeriod = 1 | 2 | 3 | 4;

const ALL_PERIODS: RenderablePeriod[] = [1, 2, 3, 4];

/**
 * i18n key/fallback pair per period — shared by this component's own
 * heading and, via a direct import, `MatchAnalytics.exportPdf`'s per-period
 * capture titles, so the on-screen heading and the PDF section title are
 * always the exact same string.
 */
export const PERIOD_MAPS_LABEL_KEYS: Record<
  RenderablePeriod,
  { key: string; fallback: string }
> = {
  1: { key: "analytics.periodMapsFirstHalf", fallback: "Primer tiempo" },
  2: { key: "analytics.periodMapsSecondHalf", fallback: "Segundo tiempo" },
  3: {
    key: "analytics.periodMapsFirstExtra",
    fallback: "Primer tiempo suplementario",
  },
  4: {
    key: "analytics.periodMapsSecondExtra",
    fallback: "Segundo tiempo suplementario",
  },
};

/**
 * Periods that should get their own map block: 1..4, ascending, filtered to
 * periods holding at least one event with a `location`. Exported so both
 * on-screen mount sites (`AnalyticsView`, `PublicMatchAnalytics`) and
 * `MatchAnalytics.exportPdf` use the exact same list — the three can never
 * disagree about which blocks exist.
 */
export function getRenderedPeriods(events: MatchEvent[]): RenderablePeriod[] {
  const periodsWithLocation = new Set<number>();
  for (const event of events) {
    if (event.location) periodsWithLocation.add(event.period);
  }
  return ALL_PERIODS.filter((period) => periodsWithLocation.has(period));
}

/** The translated heading text for one period's block. */
export function periodMapsLabel(
  period: RenderablePeriod,
  t: (key: string, fallback?: string) => string,
): string {
  const { key, fallback } = PERIOD_MAPS_LABEL_KEYS[period];
  return t(key, fallback);
}

/**
 * EPIC-97 AC6 (client wave 2026-09-25): periods 2 (second half) and 4
 * (second extra-time period) render their heatmaps/control maps rotated 180
 * degrees, matching the pitch orientation the client actually saw the team
 * attack in after "Voltear cancha" flips the cockpit for the second half of
 * each stage. Periods 1 and 3 render unrotated, as before.
 */
export function isRotatedPeriod(period: RenderablePeriod): boolean {
  return period === 2 || period === 4;
}

/**
 * Same hand-substitution pattern this codebase already uses for a `t`
 * signature with no interpolation-options parameter (see
 * `PublicTeamSheet.tsx`'s `interpolateCount`) — substitutes
 * `TeamControlMap`'s own `{{period}}` token by hand after the lookup.
 */
const interpolatePeriod = (text: string, period: string): string =>
  text.split("{{period}}").join(period);

interface PeriodMapsSectionProps {
  match: Match;
  events: MatchEvent[];
  period: RenderablePeriod;
  /**
   * PMA-PUB-014-style share-section gating, same shape `HeatMapSection`
   * uses: absent (the cockpit's own `AnalyticsView` mount never passes it)
   * means `gate` is a no-op identity function and the DOM is unchanged.
   */
  sectionState?: (key: ShareSectionKey) => ShareVisibilityState;
  upsell?: ShareUpsell;
  language?: "en" | "es";
  t: (key: string, fallback?: string) => string;
}

const PeriodMapsSection: FC<PeriodMapsSectionProps> = ({
  match,
  events,
  period,
  sectionState,
  upsell,
  language,
  t,
}) => {
  const gate = (key: ShareSectionKey, node: ReactNode): ReactNode => {
    if (!sectionState || !upsell || !language) return node;
    return (
      <SectionGate
        sectionKey={key}
        state={sectionState(key)}
        upsell={upsell}
        language={language}
      >
        {node}
      </SectionGate>
    );
  };

  const homeTeamId = match.home_team.id;
  const awayTeamId = match.away_team.id;

  // EPIC-97 AC6: periods 2/4 render rotated (display-only) — see
  // `isRotatedPeriod`'s own doc comment above.
  const rotated = isRotatedPeriod(period);
  const controlMapCaption = interpolatePeriod(
    t(
      "analytics.controlMapCaptionPeriod",
      "Share of each team's located events by pitch third and lane, oriented to its attacking direction, for the {{period}}.",
    ),
    periodMapsLabel(period, t),
  );

  // Filtered once per period — every map below reads only this period's
  // events, never the full match (AC "each per-period block's maps are
  // computed only from that period's events").
  const periodEvents = useMemo(
    () => events.filter((event) => event.period === period),
    [events, period],
  );

  const homeData = useMemo(
    () => computeHeatMapData(periodEvents, homeTeamId),
    [periodEvents, homeTeamId],
  );
  const homePoints = useMemo(
    () => extractHeatPoints(periodEvents, homeTeamId),
    [periodEvents, homeTeamId],
  );
  const awayData = useMemo(
    () => computeHeatMapData(periodEvents, awayTeamId),
    [periodEvents, awayTeamId],
  );
  const awayPoints = useMemo(
    () => extractHeatPoints(periodEvents, awayTeamId),
    [periodEvents, awayTeamId],
  );

  const heatMapsNode = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SoccerFieldHeatMap
        data={homeData}
        points={homePoints}
        title={match.home_team.short_name || match.home_team.name}
        accentColor="#10b981"
        team={match.home_team}
        side="home"
        rotated={rotated}
        t={t}
        data-testid={`period-heatmap-home-${period}`}
      />
      <SoccerFieldHeatMap
        data={awayData}
        points={awayPoints}
        title={match.away_team.short_name || match.away_team.name}
        accentColor="#6366f1"
        team={match.away_team}
        side="away"
        rotated={rotated}
        t={t}
        data-testid={`period-heatmap-away-${period}`}
      />
    </div>
  );

  const controlMapNode = (
    <TeamControlMap
      match={match}
      events={periodEvents}
      t={t}
      rotated={rotated}
      captionOverride={controlMapCaption}
      data-testid={`period-team-control-map-${period}`}
    />
  );

  // EPIC-110 D2/D6: one row per team (strip, heat map, zone grid, thirds
  // pitch) when both share sections are visible (or the page is ungated). A
  // blurred/hidden section keeps the legacy, singly-gated blocks so each
  // SectionGate still wraps one contiguous node.
  const allVisible =
    !sectionState ||
    (sectionState("heatmaps") === "visible" &&
      sectionState("area_control") === "visible");
  const rowsNode = (
    <TeamPitchRows
      match={match}
      events={periodEvents}
      t={t}
      rotated={rotated}
      captionOverride={controlMapCaption}
      controlTestId={`period-team-control-map-${period}`}
      ids={{
        home: {
          heat: `period-heatmap-home-${period}`,
          thirds: `period-thirds-pitch-home-${period}`,
          strip: `zone-strip-home-${period}`,
          row: `team-pitch-row-home-${period}`,
        },
        away: {
          heat: `period-heatmap-away-${period}`,
          thirds: `period-thirds-pitch-away-${period}`,
          strip: `zone-strip-away-${period}`,
          row: `team-pitch-row-away-${period}`,
        },
      }}
    />
  );

  // Mirrors HeatMapSection's own empty-chrome guard (PMA-COCK-186 round-1
  // judge fix): when both sections this block gates are hidden, render
  // nothing at all — heading included — rather than an empty card. Only
  // reachable when the caller passed `sectionState` (the public page); the
  // cockpit's own mount never does.
  if (
    sectionState &&
    sectionState("heatmaps") === "hidden" &&
    sectionState("area_control") === "hidden"
  ) {
    return null;
  }

  return (
    <div
      className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 space-y-6"
      data-testid={`period-maps-section-${period}`}
    >
      <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">
        {periodMapsLabel(period, t)}
      </h3>
      {allVisible ? (
        rowsNode
      ) : (
        <>
          {gate("heatmaps", heatMapsNode)}
          {gate("area_control", controlMapNode)}
        </>
      )}
    </div>
  );
};

export default PeriodMapsSection;
